import React, { useState, useEffect } from "react";
import { Zap, Tv, ChevronRight, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { database } from "../../Config/firebase";
import { ref, onValue, update } from "../../Config/matuRtdb";
import BetModal from "../betting/components/BetModal";
import FloatingBetButton from "../betting/components/FloatingBetButton";

export default function LiveSportsContent() {
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSport, setFilterSport] = useState(null);
  const [selectedBets, setSelectedBets] = useState([]);
  const [betAmount, setBetAmount] = useState("");
  const [showBetModal, setShowBetModal] = useState(false);
  const [selectedButtonsMap, setSelectedButtonsMap] = useState({});
  const [matchTimers, setMatchTimers] = useState({});
  const [matchStates, setMatchStates] = useState({});

  // Sports filter options
  const sportFilters = ["Fútbol", "Baloncesto", "Tenis", "Voleibol", "Hockey"];

  // Constantes para la duración de los partidos (en millisegundos)
  const FIRST_HALF_DURATION = 2 * 60 * 1000; // 2 minutos
  const HALFTIME_DURATION = 1 * 60 * 1000; // 1 minuto
  const SECOND_HALF_DURATION = 2 * 60 * 1000; // 2 minutos
  const TOTAL_DURATION =
    FIRST_HALF_DURATION + HALFTIME_DURATION + SECOND_HALF_DURATION;

  useEffect(() => {
    // Reference to the matches in Firebase
    const matchesRef = ref(database, "/App/betting/match");

    // Listen for changes in the data
    const unsubscribe = onValue(
      matchesRef,
      (snapshot) => {
        const data = snapshot.val();
        const matches = [];

        // Verificar si hay datos
        if (data) {
          // Procesar cada partido directamente del objeto principal
          Object.keys(data).forEach((key) => {
            const match = data[key];

            // Solo incluir partidos en vivo y verificar que la fecha sea hoy o que las horas sean cercanas
            const today = new Date();
            const matchDate = match.date
              ? new Date(match.date.split("/").reverse().join("-"))
              : null;

            const matchHour = match.hours
              ? parseInt(match.hours.split(":")[0])
              : null;
            const currentHour = today.getHours();

            const isToday = matchDate
              ? matchDate.toDateString() === today.toDateString()
              : false;

            const isNearbyHour = matchHour
              ? Math.abs(matchHour - currentHour) <= 2
              : false;

            // Solo mostrar partidos que estén en vivo o próximos y no hayan finalizado
            if (
              (match.isLive === true || (isToday && isNearbyHour)) &&
              match.finished !== true
            ) {
              matches.push({
                id: key,
                ...match,
                // Inicializar score si no existe
                homeTeam: {
                  ...match.homeTeam,
                  score: match.homeTeam.score || "0",
                },
                awayTeam: {
                  ...match.awayTeam,
                  score: match.awayTeam.score || "0",
                },
              });
            }
          });
        }
        setLiveMatches(matches);
        setLoading(false);

        // Inicializar los estados de los partidos si no existen
        const initialMatchStates = {};
        matches.forEach((match) => {
          if (!matchStates[match.id]) {
            initialMatchStates[match.id] = {
              gameState: match.isLive ? "firstHalf" : "notStarted",
              elapsedTime: 0,
              startTime: match.isLive ? Date.now() : null,
              homeScore: parseInt(match.homeTeam.score) || 0,
              awayScore: parseInt(match.awayTeam.score) || 0,
              lastUpdate: Date.now(),
            };
          }
        });

        if (Object.keys(initialMatchStates).length > 0) {
          setMatchStates((prevStates) => ({
            ...prevStates,
            ...initialMatchStates,
          }));
        }
      },
      (error) => {
        console.error("Error fetching matches:", error);
        setLoading(false);
      }
    );

    // Clean up listener on unmount
    return () => unsubscribe();
  }, []);

  // Iniciar simulación de partidos
  useEffect(() => {
    // Iniciar simuladores para partidos en vivo
    liveMatches.forEach((match) => {
      if (match.isLive && !matchTimers[match.id]) {
        startMatchSimulation(match);
      }
    });

    // Cleanup de timers cuando se desmonta el componente
    return () => {
      Object.values(matchTimers).forEach((timer) => clearInterval(timer));
    };
  }, [liveMatches]);

  // Función para iniciar la simulación de un partido
  const startMatchSimulation = (match) => {
    // Si ya hay un timer para este partido, no crear uno nuevo
    if (matchTimers[match.id]) return;

    // Estado inicial del partido
    const initialState = matchStates[match.id] || {
      gameState: "firstHalf",
      elapsedTime: 0,
      startTime: Date.now(),
      homeScore: parseInt(match.homeTeam.score) || 0,
      awayScore: parseInt(match.awayTeam.score) || 0,
      lastUpdate: Date.now(),
    };

    setMatchStates((prev) => ({
      ...prev,
      [match.id]: initialState,
    }));

    // Crear un timer para actualizar el estado del partido cada segundo
    const timer = setInterval(() => {
      updateMatchState(match.id);
    }, 1000);

    setMatchTimers((prev) => ({
      ...prev,
      [match.id]: timer,
    }));
  };

  // Actualizar el estado del partido
  const updateMatchState = (matchId) => {
    setMatchStates((prev) => {
      const currentState = prev[matchId];
      if (!currentState) return prev;

      const now = Date.now();
      const elapsedTime = now - currentState.startTime;

      // Determinar el estado actual del partido basado en el tiempo transcurrido
      let newGameState = currentState.gameState;
      let newHomeScore = currentState.homeScore;
      let newAwayScore = currentState.awayScore;

      if (elapsedTime < FIRST_HALF_DURATION) {
        newGameState = "firstHalf";
      } else if (elapsedTime < FIRST_HALF_DURATION + HALFTIME_DURATION) {
        newGameState = "halftime";
      } else if (elapsedTime < TOTAL_DURATION) {
        newGameState = "secondHalf";
      } else {
        newGameState = "finished";
        // Finalizar el partido en Firebase
        finishMatch(matchId, newHomeScore, newAwayScore);
        // Limpiar el timer
        if (matchTimers[matchId]) {
          clearInterval(matchTimers[matchId]);
          setMatchTimers((prev) => {
            const newTimers = { ...prev };
            delete newTimers[matchId];
            return newTimers;
          });
        }
      }

      // Generar goles aleatorios durante el juego (pero no en el descanso)
      if (
        (newGameState === "firstHalf" || newGameState === "secondHalf") &&
        now - currentState.lastUpdate > 30000 && // Revisar cada 30 segundos para posibles goles
        Math.random() < 0.3
      ) {
        // 30% de probabilidad de gol

        // Determinar qué equipo marca (50/50)
        if (Math.random() < 0.5) {
          // Limitar a máximo 2 goles por equipo
          if (newHomeScore < 2) {
            newHomeScore += 1;
          }
        } else {
          if (newAwayScore < 2) {
            newAwayScore += 1;
          }
        }

        // Actualizar marcadores en Firebase
        updateMatchScore(matchId, newHomeScore, newAwayScore);
      }

      return {
        ...prev,
        [matchId]: {
          ...currentState,
          gameState: newGameState,
          elapsedTime: elapsedTime,
          homeScore: newHomeScore,
          awayScore: newAwayScore,
          lastUpdate: now,
        },
      };
    });
  };

  // Actualizar el marcador en Firebase
  const updateMatchScore = (matchId, homeScore, awayScore) => {
    const match = liveMatches.find((m) => m.id === matchId);
    if (!match) return;

    const matchRef = ref(database, `/App/betting/match/${matchId}`);
    update(matchRef, {
      "homeTeam/score": homeScore.toString(),
      "awayTeam/score": awayScore.toString(),
    });
  };

  // Finalizar el partido en Firebase
  const finishMatch = (matchId, homeScore, awayScore) => {
    const matchRef = ref(database, `/App/betting/match/${matchId}`);
    update(matchRef, {
      isLive: false,
      finished: true,
      "homeTeam/score": homeScore.toString(),
      "awayTeam/score": awayScore.toString(),
    });
  };

  // Formatear el tiempo de juego para mostrar
  const formatGameTime = (matchId) => {
    const state = matchStates[matchId];
    if (!state) return "00:00";

    let displayTime;
    if (state.gameState === "notStarted") {
      return "No iniciado";
    } else if (state.gameState === "halftime") {
      return "Descanso";
    } else if (state.gameState === "finished") {
      return "Finalizado";
    }

    // Calcular minutos y segundos
    let seconds;
    if (state.gameState === "firstHalf") {
      seconds = Math.floor(state.elapsedTime / 1000);
    } else if (state.gameState === "secondHalf") {
      seconds =
        Math.floor(
          (state.elapsedTime - FIRST_HALF_DURATION - HALFTIME_DURATION) / 1000
        ) + Math.floor(FIRST_HALF_DURATION / 1000);
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Filter reset handler
  const handleClearFilter = () => {
    setFilterSport(null);
  };

  // Filter selection handler
  const handleFilterSelect = (sport) => {
    setFilterSport(filterSport === sport ? null : sport);
  };

  // Get matches based on current filter
  const filteredMatches = filterSport
    ? liveMatches.filter((match) => match.sport === filterSport)
    : liveMatches;

  // Handle bet click
  const handleBetClick = (match, betType, odd) => {
    // Check if this match already has a selected bet
    const existingBetIndex = selectedBets.findIndex(
      (bet) => bet.matchId === match.id
    );

    // Toggle selection if clicking on the same bet option
    if (
      existingBetIndex !== -1 &&
      selectedBets[existingBetIndex].type === betType
    ) {
      // Remove the bet
      const newSelectedBets = [...selectedBets];
      newSelectedBets.splice(existingBetIndex, 1);
      setSelectedBets(newSelectedBets);

      // Update selection map
      setSelectedButtonsMap((prev) => {
        const updated = { ...prev };
        delete updated[match.id];
        return updated;
      });
      return;
    }

    // Create new bet object
    const newBet = {
      matchId: match.id,
      homeTeam: match.homeTeam.name,
      awayTeam: match.awayTeam.name,
      type: betType,
      odd: odd,
      matchDate: match.date,
      matchHours: match.hours,
      isLive: true,
    };

    // Update selectedBets array
    let updatedBets;
    if (existingBetIndex !== -1) {
      // Replace existing bet for this match
      updatedBets = [...selectedBets];
      updatedBets[existingBetIndex] = newBet;
    } else {
      // Add new bet
      updatedBets = [...selectedBets, newBet];
    }

    setSelectedBets(updatedBets);

    // Update selected buttons map for UI highlighting
    setSelectedButtonsMap((prev) => ({
      ...prev,
      [match.id]: betType,
    }));
  };

  // Remove a bet from the selection
  const removeBet = (matchId) => {
    setSelectedBets((prev) => prev.filter((bet) => bet.matchId !== matchId));

    // Update selection map
    setSelectedButtonsMap((prev) => {
      const updated = { ...prev };
      delete updated[matchId];
      return updated;
    });
  };

  // Calculate combined odds
  const calculateCombinedOdds = () => {
    if (selectedBets.length === 0) return 0;
    return selectedBets
      .reduce((acc, bet) => acc * parseFloat(bet.odd), 1)
      .toFixed(2);
  };

  // Handle bet submission
  const handleSubmitBet = () => {
    if (!betAmount || isNaN(betAmount) || parseFloat(betAmount) <= 0) {
      alert("Por favor ingresa un monto válido");
      return;
    }

    alert(`Apuesta combinada realizada por $${betAmount}`);
    setShowBetModal(false);
    setBetAmount("");
    setSelectedBets([]);
    setSelectedButtonsMap({});
  };

  // Open the betting modal
  const openBetModal = () => {
    if (selectedBets.length === 0) {
      alert("Por favor selecciona al menos una apuesta");
      return;
    }
    setShowBetModal(true);
  };

  // Función para determinar la fase del partido (texto descriptivo)
  const getMatchPhase = (matchId) => {
    const state = matchStates[matchId];
    if (!state) return "";

    switch (state.gameState) {
      case "notStarted":
        return "Por iniciar";
      case "firstHalf":
        return "1er Tiempo";
      case "halftime":
        return "Descanso";
      case "secondHalf":
        return "2do Tiempo";
      case "finished":
        return "Finalizado";
      default:
        return "";
    }
  };

  return (
    <div className="px-3 pt-3 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center mb-4">
          <Zap className="w-6 h-6 text-orange-500 mr-2" />
          <h1 className="text-white text-xl font-bold">Deportes en Vivo</h1>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <div className="text-white font-medium">Filtros</div>
            <motion.button
              className="text-xs bg-orange-500 rounded px-2 py-1 text-white"
              whileTap={{ scale: 0.95 }}
              onClick={handleClearFilter}
            >
              Limpiar
            </motion.button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {sportFilters.map((sport) => (
              <motion.button
                key={sport}
                className={`px-3 py-1 rounded-full text-xs text-white whitespace-nowrap ${
                  filterSport === sport ? "bg-orange-500" : "bg-gray-700"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleFilterSelect(sport)}
              >
                {sport}
              </motion.button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="text-white">Cargando partidos en vivo...</div>
          </div>
        ) : filteredMatches.length > 0 ? (
          <AnimatePresence>
            <div className="space-y-3 mb-6">
              {filteredMatches.map((match) => {
                const matchState = matchStates[match.id];
                const gameTime = formatGameTime(match.id);
                const matchPhase = getMatchPhase(match.id);

                return (
                  <motion.div
                    key={match.id}
                    className="bg-gray-800 rounded-lg p-3 shadow-md"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        {matchState && matchState.gameState !== "finished" ? (
                          <>
                            <span className="bg-red-500 h-2 w-2 rounded-full mr-2"></span>
                            <span className="text-gray-400 text-xs">
                              EN VIVO • {matchPhase}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            {matchPhase} • {match.hours}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-xs">
                        <Clock className="w-3 h-3 mr-1 text-gray-400" />
                        <span className="text-white font-medium">
                          {gameTime}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full mr-2 overflow-hidden bg-gray-700 flex items-center justify-center">
                          {match.homeTeam.logo ? (
                            <img
                              src={match.homeTeam.logo}
                              alt={match.homeTeam.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-xs">
                              {match.homeTeam.name.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-white text-sm">
                            {match.homeTeam.name}
                          </span>
                          <span className="text-green-500 text-xs font-semibold">
                            {matchState
                              ? matchState.homeScore
                              : match.homeTeam.score || "0"}
                          </span>
                        </div>
                      </div>
                      <span className="text-white font-medium mx-2">vs</span>
                      <div className="flex items-center">
                        <div className="flex flex-col items-end">
                          <span className="text-white text-sm">
                            {match.awayTeam.name}
                          </span>
                          <span className="text-green-500 text-xs font-semibold">
                            {matchState
                              ? matchState.awayScore
                              : match.awayTeam.score || "0"}
                          </span>
                        </div>
                        <div className="w-6 h-6 rounded-full ml-2 overflow-hidden bg-gray-700 flex items-center justify-center">
                          {match.awayTeam.logo ? (
                            <img
                              src={match.awayTeam.logo}
                              alt={match.awayTeam.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-xs">
                              {match.awayTeam.name.charAt(0)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Solo mostrar opciones de apuesta si el partido no ha finalizado */}
                    {(!matchState || matchState.gameState !== "finished") && (
                      <div className="flex justify-between mt-3">
                        <motion.button
                          className={`text-white text-xs py-1 px-3 rounded flex-1 mr-1 ${
                            selectedButtonsMap[match.id] === "home"
                              ? "bg-orange-500"
                              : "bg-gray-700"
                          }`}
                          whileTap={{ scale: 0.95 }}
                          onClick={() =>
                            handleBetClick(match, "home", match.homeTeam.odd)
                          }
                        >
                          <div className="flex justify-between">
                            <span>Local</span>
                            <span className="font-medium">
                              {match.homeTeam.odd}
                            </span>
                          </div>
                        </motion.button>
                        <motion.button
                          className={`text-white text-xs py-1 px-3 rounded flex-1 mx-1 ${
                            selectedButtonsMap[match.id] === "draw"
                              ? "bg-orange-500"
                              : "bg-gray-700"
                          }`}
                          whileTap={{ scale: 0.95 }}
                          onClick={() =>
                            handleBetClick(
                              match,
                              "draw",
                              match.drawOdd || "3.10"
                            )
                          }
                        >
                          <div className="flex justify-between">
                            <span>Empate</span>
                            <span className="font-medium">
                              {match.drawOdd || "3.10"}
                            </span>
                          </div>
                        </motion.button>
                        <motion.button
                          className={`text-white text-xs py-1 px-3 rounded flex-1 ml-1 ${
                            selectedButtonsMap[match.id] === "away"
                              ? "bg-orange-500"
                              : "bg-gray-700"
                          }`}
                          whileTap={{ scale: 0.95 }}
                          onClick={() =>
                            handleBetClick(match, "away", match.awayTeam.odd)
                          }
                        >
                          <div className="flex justify-between">
                            <span>Visita</span>
                            <span className="font-medium">
                              {match.awayTeam.odd}
                            </span>
                          </div>
                        </motion.button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </AnimatePresence>
        ) : (
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col items-center justify-center h-32">
            <Tv className="w-8 h-8 text-gray-600 mb-2" />
            <div className="text-white text-center">
              No hay partidos en vivo en este momento
            </div>
            <div className="text-gray-400 text-xs text-center mt-1">
              Vuelve más tarde para ver las transmisiones en vivo
            </div>
          </div>
        )}
      </motion.div>
      {/* Betting Modal */}
      <BetModal
        show={showBetModal}
        onClose={() => setShowBetModal(false)}
        selectedBets={selectedBets}
        removeBet={removeBet}
        calculateCombinedOdds={calculateCombinedOdds}
        betAmount={betAmount}
        setBetAmount={setBetAmount}
        onSubmit={handleSubmitBet}
      />
      {/* Floating Action Button for Combined Bets */}
      <FloatingBetButton
        selectedBetsCount={selectedBets.length}
        onOpen={openBetModal}
      />
    </div>
  );
}
